import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, RefreshCw, Trash2, FileText, AlertTriangle, Check, Plus, X, Cpu, Key, ChevronDown, ChevronRight, Download } from 'react-feather';
import { apiClient } from '../../shared/api/client';
import ReceiptModal, { ReceiptView } from '../../components/ReceiptModal';

interface MailAccount {
  id: number;
  label: string;
  host: string;
  port: number;
  username: string;
  folder: string;
  lastSyncAt?: string | null;
  lastError?: string | null;
}

interface Receipt {
  id: number;
  vendor: string;
  filename?: string | null;
  amount?: number | null;
  currency?: string | null;
  confidence?: string | null;
  hasFile: boolean;
  date?: string | null;
  sentAt?: string | null;
  subject?: string | null;
  note?: string | null;
  matchReason?: string | null;
  url?: string | null;
  transaction?: { date: string | null; amount: number; name: string } | null;
}

interface ExpenseRow {
  id: number;
  date: string | null;
  name: string;
  amount: number;
  submittedAt: string | null;
  receipt: Receipt | null;
}

interface Suggestion {
  receiptId: number;
  vendor: string;
  amount?: number | null;
  currency?: string | null;
  date?: string | null;
  reason?: string | null;
  transaction: { id: number; date: string | null; amount: number; name: string };
}

interface Orphan {
  id: number;
  vendor: string;
  subject?: string | null;
  filename?: string | null;
  amount?: number | null;
  currency?: string | null;
  date?: string | null;
  sentAt?: string | null;
  hasFile: boolean;
  note?: string | null;
  url?: string | null;
}

const PRESETS: Record<string, { host: string; port: number; hint: string }> = {
  'icloud.com': { host: 'imap.mail.me.com', port: 993, hint: 'iCloud kräver ett appspecifikt lösenord från appleid.apple.com' },
  'me.com': { host: 'imap.mail.me.com', port: 993, hint: 'iCloud kräver ett appspecifikt lösenord från appleid.apple.com' },
  'gmail.com': { host: 'imap.gmail.com', port: 993, hint: 'Gmail kräver ett app-lösenord' },
  'jakompetens.se': { host: 'imap.one.com', port: 993, hint: 'Mailen ligger hos one.com — vanligt lösenord fungerar' },
};

const emptyForm = { label: '', host: '', port: '993', username: '', password: '', folder: 'INBOX' };

// Receipts fetched from the user's own mailboxes, lined up against the work
// expenses they belong to. The app reads mail; it never sends or deletes.
const ReceiptsPage: React.FC = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<MailAccount[]>([]);
  const [expenses, setExpenses] = useState<ExpenseRow[]>([]);
  const [orphans, setOrphans] = useState<Orphan[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [hasKey, setHasKey] = useState(false);
  const [keyInput, setKeyInput] = useState('');
  const [showKeyForm, setShowKeyForm] = useState(false);
  const [aiRunning, setAiRunning] = useState(false);
  const [aiProgress, setAiProgress] = useState<string | null>(null);
  const [aiDays, setAiDays] = useState('180');
  const [viewing, setViewing] = useState<ReceiptView | null>(null);
  const [showSubmitted, setShowSubmitted] = useState(false);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [zipping, setZipping] = useState(false);

  const load = async () => {
    try {
      const [accRes, recRes, aiRes] = await Promise.all([
        apiClient.get('/mail/accounts'),
        apiClient.get('/mail/reconciliation'),
        apiClient.get('/mail/ai/status'),
      ]);
      setAccounts((accRes as any).accounts || []);
      setHasKey(!!(aiRes as any).hasKey);
      setExpenses((recRes as any).expenses || []);
      setOrphans((recRes as any).orphans || []);
      setSuggestions((recRes as any).suggestions || []);
    } catch (e: any) {
      // Without this the page renders its empty states and looks as if the
      // mail account and the receipts were gone, when the server simply
      // refused to answer.
      console.error('Load receipts error:', e);
      setError(e?.response?.status === 429
        ? 'Servern svarar inte just nu (för många anrop). Vänta en stund och ladda om.'
        : 'Kunde inte hämta kvittouppgifterna.');
    }
  };

  useEffect(() => {
    load();
    // A run started before a reload is still going on the server.
    apiClient.get('/mail/ai/job').then((res: any) => {
      if (res.job?.status === 'running') { setAiRunning(true); setAiProgress(res.job.progress); setTimeout(() => pollJob(), 3000); }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filling in the server from the address saves the part nobody remembers
  const onUsernameBlur = () => {
    const domain = (form.username.split('@')[1] || '').toLowerCase();
    const preset = PRESETS[domain];
    if (preset && !form.host) {
      setForm(f => ({ ...f, host: preset.host, port: String(preset.port) }));
    }
  };

  const hint = PRESETS[(form.username.split('@')[1] || '').toLowerCase()]?.hint;

  const saveAccount = async () => {
    setError(null);
    if (!form.username || !form.password || !form.host) {
      setError('Fyll i adress, server och lösenord.');
      return;
    }
    try {
      setSaving(true);
      await apiClient.post('/mail/accounts', {
        label: form.label || form.username,
        host: form.host,
        port: parseInt(form.port, 10) || 993,
        username: form.username,
        password: form.password,
        folder: form.folder || 'INBOX',
      });
      setForm(emptyForm);
      setShowForm(false);
      setInfo('Kontot sparades och inloggningen fungerade.');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Kunde inte spara kontot.');
    } finally {
      setSaving(false);
    }
  };

  const removeAccount = async (id: number) => {
    try {
      await apiClient.delete(`/mail/accounts/${id}`);
      await load();
    } catch (e) {
      setError('Kunde inte ta bort kontot.');
    }
  };

  const sync = async () => {
    setError(null); setInfo(null);
    try {
      setSyncing(true);
      const res: any = await apiClient.post('/mail/sync', {});
      setInfo(res.message || 'Klart.');
      if (res.errors) setError(res.errors.join(' · '));
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Kunde inte hämta kvitton.');
    } finally {
      setSyncing(false);
    }
  };

  const saveKey = async () => {
    setError(null);
    try {
      await apiClient.post('/mail/ai/key', { apiKey: keyInput.trim() });
      setKeyInput(''); setShowKeyForm(false); setInfo('Nyckeln sparades.');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Kunde inte spara nyckeln.');
    }
  };

  // The run takes minutes, so the request only starts it. Progress comes from
  // polling — a held-open request would be killed by the proxy long before the
  // work finished, which is exactly what happened before.
  // A run can last an hour. Asking every few seconds the whole way through is
  // thousands of pointless calls, so the interval backs off once it is clear
  // this is not the kind of job that finishes quickly.
  const pollDelay = (started: number) => {
    const elapsed = Date.now() - started;
    if (elapsed < 60_000) return 5000;
    if (elapsed < 10 * 60_000) return 15_000;
    return 30_000;
  };

  const pollJob = async (started = Date.now()) => {
    try {
      const res: any = await apiClient.get('/mail/ai/job');
      const job = res.job;
      if (!job) { setAiRunning(false); return; }
      setAiProgress(job.progress || null);
      if (job.status === 'running') {
        setAiRunning(true);
        setTimeout(() => pollJob(started), pollDelay(started));
      } else {
        setAiRunning(false);
        if (job.status === 'error') setError(job.progress || 'AI-analysen misslyckades.');
        else setInfo(job.progress || 'Klart.');
        await load();
      }
    } catch {
      setAiRunning(false);
    }
  };

  const runAi = async () => {
    setError(null); setInfo(null); setAiProgress('Startar…');
    try {
      setAiRunning(true);
      await apiClient.post('/mail/ai/sync', { sinceDays: parseInt(aiDays, 10) });
      setTimeout(() => pollJob(), 2000);
    } catch (e: any) {
      setAiRunning(false);
      setError(e?.response?.data?.error || 'Kunde inte starta analysen.');
    }
  };

  const answerSuggestion = async (receiptId: number, accept: boolean) => {
    try {
      await apiClient.post(`/mail/receipts/${receiptId}/suggestion`, { accept });
      await load();
    } catch (e) {
      setError('Kunde inte spara svaret.');
    }
  };

  const kr = (n: number) =>
    new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + ' kr';

  const missing = expenses.filter(e => !e.receipt);
  const shown = onlyMissing ? missing : expenses;
  // Anything already sent to the employer is settled business. It stays
  // reachable, but it should not compete for attention with the claims that
  // still need a receipt.
  const pending = shown.filter(e => !e.submittedAt);
  const submitted = shown.filter(e => e.submittedAt);
  const submittedSum = submitted.reduce((s, e) => s + e.amount, 0);

  // Selection only makes sense where there is a file to put in the archive.
  const selectable = shown.filter(e => e.receipt?.hasFile);
  const toggle = (receiptId: number) => setPicked(p => {
    const next = new Set(p);
    if (next.has(receiptId)) next.delete(receiptId); else next.add(receiptId);
    return next;
  });

  const downloadPicked = async () => {
    if (picked.size === 0) return;
    setError(null); setZipping(true);
    try {
      const blob = await apiClient.postBlob('/mail/receipts/download', { ids: Array.from(picked) });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kvitton-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setInfo(`${picked.size} kvitton nedladdade som zip.`);
      setPicked(new Set());
    } catch {
      setError('Kunde inte ladda ner kvittona.');
    } finally { setZipping(false); }
  };

  const expenseRow = (e: ExpenseRow) => (
    <div key={e.id} className="py-2.5 flex items-start gap-3">
      {e.receipt?.hasFile ? (
        <input
          type="checkbox"
          checked={picked.has(e.receipt.id)}
          onChange={() => toggle(e.receipt!.id)}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 flex-shrink-0"
        />
      ) : (
        <span className="w-4 flex-shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm text-gray-900 truncate">{e.name}</p>
        <p className="text-xs text-gray-500">
          {e.date}
          {e.submittedAt && (
            <span className="text-green-700">
              {' · '}<Check size={11} className="inline -mt-0.5" /> inskickat {e.submittedAt}
            </span>
          )}
        </p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-sm font-medium text-gray-900">{kr(e.amount)}</p>
        {e.receipt ? (
          <button
            onClick={() => setViewing({ ...e.receipt!, transactionId: e.id })}
            className="text-xs text-blue-600"
          >
            <FileText size={11} className="inline mr-0.5 -mt-0.5" />
            {e.receipt.hasFile ? 'kvitto' : 'bara mail'}
            {e.receipt.confidence && e.receipt.confidence !== 'säker' && ` (${e.receipt.confidence})`}
          </button>
        ) : (
          // An expense with no receipt is exactly where uploading one by hand
          // belongs, so it opens the same panel.
          <button
            onClick={() => setViewing({
              id: 0, vendor: e.name, hasFile: false, transactionId: e.id,
              transaction: { date: e.date, amount: e.amount, name: e.name },
            })}
            className="text-xs text-amber-700 hover:underline"
          >
            saknar kvitto
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="page-header bg-white shadow-sm border-b border-gray-200">
        <div className="page-header-content px-4 py-4 flex items-center">
          <button onClick={() => navigate('/settings')} className="mr-4"><span className="text-xl">‹</span></button>
          <h1 className="text-xl font-bold text-gray-900">Kvitton</h1>
        </div>
      </div>

      <div className="page-content px-4 py-6 space-y-4">
        <p className="text-sm text-gray-600">
          Hämtar kvitton från din e-post och ställer dem mot dina jobbutlägg. Appen läser
          bara — inget flyttas, markeras eller raderas i mailen.
        </p>

        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2 flex items-start justify-between gap-2">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-500 flex-shrink-0"><X size={14} /></button>
          </p>
        )}
        {info && (
          <p className="text-sm text-green-800 bg-green-50 border border-green-100 rounded-lg px-3 py-2">{info}</p>
        )}

        {/* Accounts */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h2 className="font-semibold text-gray-900 mb-3">E-postkonton</h2>
          {accounts.length === 0 && !showForm && (
            <p className="text-sm text-gray-500 mb-3">Inget konto kopplat än.</p>
          )}
          {accounts.map(a => (
            <div key={a.id} className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2 mb-2">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  <Mail size={13} className="inline mr-1.5 -mt-0.5 text-gray-500" />{a.username}
                </p>
                <p className="text-xs text-gray-500">
                  {a.host}:{a.port} · {a.folder}
                  {a.lastSyncAt ? ` · senast hämtat ${new Date(a.lastSyncAt).toLocaleDateString('sv-SE')}` : ' · aldrig hämtat'}
                </p>
                {a.lastError && <p className="text-xs text-red-600 mt-0.5">{a.lastError}</p>}
              </div>
              <button onClick={() => removeAccount(a.id)} className="text-gray-400 hover:text-red-600">
                <Trash2 size={16} />
              </button>
            </div>
          ))}

          {showForm ? (
            <div className="space-y-2 mt-3">
              <input
                type="email" placeholder="E-postadress" value={form.username}
                onChange={e => setForm({ ...form, username: e.target.value })}
                onBlur={onUsernameBlur}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="text" placeholder="IMAP-server" value={form.host}
                  onChange={e => setForm({ ...form, host: e.target.value })}
                  className="col-span-2 w-full border border-gray-300 rounded-lg px-3 py-2"
                />
                <input
                  type="number" placeholder="Port" value={form.port}
                  onChange={e => setForm({ ...form, port: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <input
                type="password" placeholder="Lösenord" value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                autoComplete="new-password"
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
              {hint && <p className="text-xs text-amber-700">{hint}</p>}
              <div className="flex gap-2">
                <button onClick={saveAccount} disabled={saving}
                  className="px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white disabled:opacity-50">
                  {saving ? 'Testar inloggningen…' : 'Spara och testa'}
                </button>
                <button onClick={() => { setShowForm(false); setForm(emptyForm); setError(null); }}
                  className="px-3 py-2 text-sm font-medium rounded-lg bg-white text-gray-600 border border-gray-200">
                  Avbryt
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowForm(true)}
              className="text-sm font-medium text-blue-600 mt-1">
              <Plus size={14} className="inline mr-1 -mt-0.5" />Lägg till konto
            </button>
          )}
        </div>

        {/* The AI pass replaces the vendor/subject rules: it reads every mail
            in the window and judges each one, so a new supplier needs no code
            change. */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h2 className="font-semibold text-gray-900 mb-1">
            <Cpu size={15} className="inline mr-2 -mt-0.5 text-blue-500" />AI-analys
          </h2>
          <p className="text-xs text-gray-500 mb-3">
            Läser igenom mailen och avgör själv vad som är ett kvitto, vilken leverantör det
            gäller och vilket utlägg det hör till. Inga förutbestämda regler.
          </p>
          {!hasKey && !showKeyForm && (
            <button onClick={() => setShowKeyForm(true)} className="text-sm font-medium text-blue-600">
              <Key size={14} className="inline mr-1 -mt-0.5" />Lägg in API-nyckel för att aktivera
            </button>
          )}
          {showKeyForm && (
            <div className="space-y-2">
              <input
                type="password" placeholder="sk-ant-…" value={keyInput}
                onChange={e => setKeyInput(e.target.value)} autoComplete="new-password"
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
              <p className="text-xs text-gray-500">
                Hämtas på console.anthropic.com. Lagras krypterad, precis som e-postlösenordet.
              </p>
              <div className="flex gap-2">
                <button onClick={saveKey} className="px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white">Spara</button>
                <button onClick={() => { setShowKeyForm(false); setKeyInput(''); }}
                  className="px-3 py-2 text-sm font-medium rounded-lg bg-white text-gray-600 border border-gray-200">Avbryt</button>
              </div>
            </div>
          )}
          {aiRunning && aiProgress && (
            <p className="text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 mb-2">
              {aiProgress}
              <span className="block text-gray-500 mt-0.5">
                Körningen fortsätter även om du lämnar sidan.
              </span>
            </p>
          )}
          {hasKey && !showKeyForm && (
            <div className="flex flex-wrap gap-2 items-center">
              <button onClick={runAi} disabled={aiRunning || accounts.length === 0}
                className="px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white disabled:opacity-50">
                <Cpu size={14} className={`inline mr-1.5 -mt-0.5 ${aiRunning ? 'animate-pulse' : ''}`} />
                {aiRunning ? 'Analyserar…' : 'Låt AI:n gå igenom mailen'}
              </button>
              <select value={aiDays} onChange={e => setAiDays(e.target.value)} disabled={aiRunning}
                className="text-sm border border-gray-300 rounded-lg px-2 py-2 bg-white">
                <option value="90">3 månader</option>
                <option value="180">6 månader</option>
                <option value="400">1 år</option>
              </select>
              <button onClick={() => setShowKeyForm(true)} className="text-xs text-gray-500 underline">byt nyckel</button>
            </div>
          )}
        </div>

        {accounts.length > 0 && (
          <button onClick={sync} disabled={syncing}
            className="w-full px-3 py-3 text-sm font-medium rounded-xl bg-white text-gray-700 border border-gray-200 disabled:opacity-50">
            <RefreshCw size={14} className={`inline mr-2 -mt-0.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Hämtar…' : 'Enkel hämtning utan AI'}
          </button>
        )}

        {/* What the model worked out but could not corroborate. Shown as a
            question rather than applied, so a guess never lands on an expense
            claim without him seeing it. */}
        {suggestions.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-4 border border-amber-200">
            <h2 className="font-semibold text-gray-900 mb-1">
              {suggestions.length === 1 ? 'En koppling behöver ditt svar' : `${suggestions.length} kopplingar behöver ditt svar`}
            </h2>
            <p className="text-xs text-gray-500 mb-3">
              AI:n har resonerat sig fram till dessa men kan inte belägga dem. Stämmer det?
            </p>
            <div className="divide-y divide-gray-100">
              {suggestions.map(sg => (
                <div key={sg.receiptId} className="py-3">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium">{sg.vendor}</span>
                    {sg.amount != null && <> · {sg.amount.toLocaleString('sv-SE')} {sg.currency || ''}</>}
                    {sg.date && <> · {sg.date}</>}
                  </p>
                  <p className="text-sm text-gray-600 mt-0.5">
                    hör ihop med <span className="font-medium">{sg.transaction.name}</span> {kr(sg.transaction.amount)} den {sg.transaction.date}?
                  </p>
                  {sg.reason && <p className="text-xs text-gray-500 mt-1 italic">{sg.reason}</p>}
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => answerSuggestion(sg.receiptId, true)}
                      className="px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white">Ja</button>
                    <button onClick={() => answerSuggestion(sg.receiptId, false)}
                      className="px-3 py-1.5 text-sm font-medium rounded-lg bg-white text-gray-600 border border-gray-200">Nej</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reconciliation */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-semibold text-gray-900">Avstämning</h2>
            <span className="text-xs text-gray-500">
              {expenses.length - missing.length} av {expenses.length} har kvitto
            </span>
          </div>

          {missing.length > 0 && (
            <button onClick={() => setOnlyMissing(v => !v)}
              className={`text-xs font-medium rounded-full border px-3 py-1.5 mb-3 ${onlyMissing ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-600 border-gray-200'}`}>
              <AlertTriangle size={12} className="inline mr-1 -mt-0.5" />
              {missing.length} saknar kvitto
            </button>
          )}

          {shown.length === 0 && <p className="text-sm text-gray-500">Inga utlägg att stämma av.</p>}

          {selectable.length > 0 && (
            <div className="flex items-center justify-between gap-3 mb-2 pb-2 border-b border-gray-100">
              <button
                onClick={() => setPicked(picked.size === selectable.length
                  ? new Set()
                  : new Set(selectable.map(e => e.receipt!.id)))}
                className="text-xs text-gray-600 hover:text-gray-900"
              >
                {picked.size === selectable.length ? 'Avmarkera alla' : `Markera alla (${selectable.length})`}
              </button>
              <button
                onClick={downloadPicked}
                disabled={picked.size === 0 || zipping}
                className="text-xs font-medium rounded-full px-3 py-1.5 bg-blue-600 text-white disabled:bg-gray-100 disabled:text-gray-400 transition-colors"
              >
                <Download size={12} className="inline mr-1 -mt-0.5" />
                {zipping ? 'Packar…' : `Ladda ner ${picked.size || ''} kvitton`.trim()}
              </button>
            </div>
          )}

          <div className="divide-y divide-gray-100">
            {pending.map(expenseRow)}
          </div>

          {submitted.length > 0 && (
            <div className={pending.length > 0 ? 'mt-3 pt-3 border-t border-gray-200' : ''}>
              <button
                onClick={() => setShowSubmitted(v => !v)}
                className="w-full flex items-center justify-between gap-2 py-1 text-left"
              >
                <span className="text-sm font-medium text-green-700 flex items-center gap-1.5">
                  {showSubmitted ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <Check size={13} />
                  {submitted.length} inskickade
                </span>
                <span className="text-xs text-gray-500">{kr(submittedSum)}</span>
              </button>
              {showSubmitted && (
                <div className="divide-y divide-gray-100 mt-1">
                  {submitted.map(expenseRow)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Receipts with no expense: either the charge is uncategorized, or
            the mail was not a receipt at all */}
        {orphans.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h2 className="font-semibold text-gray-900 mb-1">Kvitton utan utlägg</h2>
            <p className="text-xs text-gray-500 mb-3">
              Hittade inget matchande köp. Antingen är transaktionen inte kategoriserad som
              utlägg, eller så var mailet inget kvitto.
            </p>
            <div className="divide-y divide-gray-100">
              {orphans.map(o => (
                <div key={o.id} className="py-2.5 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-gray-900 truncate">{o.vendor}{o.subject ? ` — ${o.subject}` : ''}</p>
                    <p className="text-xs text-gray-500">{o.date || 'okänt datum'}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {o.amount != null && (
                      <p className="text-sm text-gray-700">
                        {o.amount.toLocaleString('sv-SE')} {o.currency || ''}
                      </p>
                    )}
                    <button onClick={() => setViewing(o)} className="text-xs text-blue-600">
                      <FileText size={11} className="inline mr-0.5 -mt-0.5" />
                      {o.hasFile ? 'kvitto' : 'bara mail'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {viewing && (
        <ReceiptModal
          receipt={viewing}
          onClose={() => setViewing(null)}
          onChanged={load}
        />
      )}
    </div>
  );
};

export default ReceiptsPage;
